import type { Line, MemoryStage, MemoryStateSummary } from '@nelnela/contracts';
import { tokenize, type Token } from './text';

/**
 * The reading surface's two marks (04-flows principle 6, decision D2, reading-surface-spec § 2):
 *
 *   dotted  a WEAK single word — the model thinks this one is shaky for this learner
 *   solid   a CHUNK: a multi-token item (a phrase or a pattern occurrence), one tap target
 *   none    a known word — the whole point is that most of the line carries nothing
 *
 * Two rules make this honest rather than decorative:
 *
 *  - **The density slider moves a threshold, not a style.** `markDensity` is the learner's
 *    tolerance for dots: 0 shows none, 1 shows every shaky word. An item is dotted when its
 *    weakness clears that bar, so "fewer ↔ more" is a real statement about how much the model is
 *    unsure of, not a cosmetic setting.
 *  - **A chunk is structural, not memory-based.** Multi-token items are always one tap target,
 *    whether or not the learner knows them — that is what makes "tap inside a chunk opens the
 *    chunk card first" possible (spec § 3).
 *
 * Suppressed items ("I already know this") are never marked at all, on either style.
 */

export interface Marks {
  /** token indices (into `tokenize(line.text)`) that get the dotted underline */
  weak: Set<number>;
  /** multi-token items: one span, one tap target, solid underline */
  chunks: ChunkSpan[];
}

export interface ChunkSpan {
  itemId: string;
  charStart: number;
  charEnd: number;
  /** the token indices the span covers, so the renderer can swallow them into one target */
  tokens: number[];
}

export interface MarkOptions {
  /** `profile.display.markDensity` (0..1) */
  density: number;
  /** `profile.display.marksHidden` — the one-tap "hide marks" */
  hidden: boolean;
}

/**
 * What an axis's STAGE alone says about weakness, before decay is considered. Retrievability
 * cannot express these two states on its own, because R is ~1 for a day after ANY review —
 * passed or failed — so a lapse would otherwise be invisible until the curve caught up.
 */
const STAGE_FLOOR: Record<MemoryStage, number> = {
  new: 0, // no evidence either way; excluded entirely when another axis has evidence
  learning: 0.5, // one signal is not knowledge (GRADUATE_REPS) — dotted at the default density
  relearning: 0.9, // tested and FAILED: weaker evidence than never having been tested
  review: 0, // graduated; decay alone decides
};

/**
 * How weak an item is, 0 (solid) … 1 (never met). The dotted mark shows when this is at least
 * `1 - density`: at density 1 anything not solid is dotted, at density 0 nothing ever is.
 *
 * Only axes that have actually been GRADED count. An axis at stage 'new' is absence of evidence,
 * not evidence of weakness — and the difference is not academic: `Watch.tsx` emits a `listen`
 * exposure for every word token it renders, the fold turns each into a `memory_state` row at
 * stage 'new', and nothing in the app can grade the listen axis yet. Letting those rows into the
 * comparison scored a word graduated on `read` at weakness 1 — identical to a word never met —
 * so every watched word was dotted at every density. Mirrors `overallFor` in `lib/fsrs.ts`.
 */
export function weakness(memory: MemoryStateSummary | undefined): number {
  if (!memory) return 1; // unmet: the model knows nothing about it
  if (memory.suppressed) return 0;
  if (memory.overall === 'solid') return 0;
  const axes = Object.values(memory.axes).filter((a): a is NonNullable<typeof a> => !!a);
  if (axes.length === 0) return 1;
  const tested = axes.filter((a) => a.stage !== 'new');
  if (tested.length === 0) return 0.9; // exposures only: met, unmeasured — nearly as weak as unmet
  // The axis the learner is weakest on decides — a word you can read but not hear is not known.
  return Math.min(
    1,
    Math.max(...tested.map((a) => Math.max(STAGE_FLOOR[a.stage], 1 - a.retrievability))),
  );
}

/** Should a word with this weakness be dotted at this density? */
export function isMarked(w: number, density: number): boolean {
  if (density <= 0) return false;
  if (density >= 1) return w > 0;
  return w >= 1 - density;
}

/**
 * Marks for one line. `line.items` absent (the resolver has never run) ⇒ no marks at all, which
 * is different from "resolved to nothing": an unresolved transcript must not look like a
 * transcript the learner knows by heart.
 */
export function marksFor(
  line: Line,
  memory: Readonly<Record<string, MemoryStateSummary>>,
  opts: MarkOptions,
  tokens: Token[] = tokenize(line.text),
): Marks {
  const empty: Marks = { weak: new Set(), chunks: [] };
  if (!line.items || opts.hidden) return empty;

  const tokenAt = (charStart: number, charEnd: number): number[] => {
    const out: number[] = [];
    for (let i = 0; i < tokens.length; i++) {
      const t = tokens[i]!;
      if (!t.word) continue;
      if (t.charEnd > charStart && t.charStart < charEnd) out.push(i);
    }
    return out;
  };

  const weak = new Set<number>();
  const chunks: ChunkSpan[] = [];
  for (const item of line.items) {
    if (item.charStart === null || item.charEnd === null) continue;
    const covered = tokenAt(item.charStart, item.charEnd);
    if (covered.length === 0) continue;
    const mem = memory[item.itemId];
    if (mem?.suppressed) continue;
    if (covered.length > 1) {
      chunks.push({
        itemId: item.itemId,
        charStart: item.charStart,
        charEnd: item.charEnd,
        tokens: covered,
      });
      continue;
    }
    if (isMarked(weakness(mem), opts.density)) weak.add(covered[0]!);
  }
  // Overlapping chunks (two patterns on the same words) would fight over the tap target; the
  // longest one wins, and the rest fall back to being ordinary words.
  chunks.sort((a, b) => b.tokens.length - a.tokens.length || a.charStart - b.charStart);
  const taken = new Set<number>();
  const kept: ChunkSpan[] = [];
  for (const c of chunks) {
    if (c.tokens.some((t) => taken.has(t))) continue;
    for (const t of c.tokens) taken.add(t);
    kept.push(c);
  }
  // A word inside a chunk carries the chunk's solid mark, never its own dotted one.
  for (const t of taken) weak.delete(t);
  return { weak, chunks: kept.sort((a, b) => a.charStart - b.charStart) };
}

/** Every item id that appears on these lines — the argument to `memoryForItems`. */
export function itemIdsOf(lines: readonly Line[]): string[] {
  const ids = new Set<string>();
  for (const l of lines) for (const i of l.items ?? []) ids.add(i.itemId);
  return [...ids];
}

/** The item a tap on token `idx` should open: the chunk that swallows it, if any. */
export function chunkAt(marks: Marks, tokenIdx: number): ChunkSpan | null {
  return marks.chunks.find((c) => c.tokens.includes(tokenIdx)) ?? null;
}

/** ○ unmet · ◐ shaky · ● solid (reading-surface-spec § 7). */
export function glyphFor(memory: MemoryStateSummary | null | undefined): '○' | '◐' | '●' {
  if (!memory) return '○';
  return memory.overall === 'solid' ? '●' : memory.overall === 'shaky' ? '◐' : '○';
}
